'use client';

import { useEffect } from 'react';

// Sitewide GA4 event tracker. Uses event delegation so we don't have to
// instrument every link individually. Fires:
//   - whatsapp_clicked: any <a href="https://wa.me/..."> or "whatsapp.com/send"
//   - signup_clicked: any <a href="/signup"> or <a href="/signup?..."> click
//   - login_clicked: any <a href="/login"> click
//   - contact_clicked: any <a href="mailto:..."> or href="tel:..." click
//   - app_store_clicked: any App Store / Google Play link (the website's own
//     go through /get-app), with the store and the link's data-placement
//     (banner, footer) so GA4 shows which one works
// Each event payload includes the source page path so we can see which page
// drove conversion in GA4.
declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

function send(eventName: string, params: Record<string, unknown>) {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  window.gtag('event', eventName, {
    page_path: window.location.pathname,
    page_location: window.location.href,
    ...params,
  });
}

// Same sender, for events that are NOT link clicks and so cannot be picked up
// by the delegated listener below — fired from the component that knows the
// thing actually happened.
//
// Why this exists: GA4's automatic `form_submit` counted 98 submissions against
// 1,080 real applications, because it watches for a browser form submit and our
// signup is a React multi-step form whose success is a server-action result.
// `application_submitted` is fired from that result instead, so the GA4 number
// matches the database. Marked as the key event in GA4.
export function trackEvent(eventName: string, params: Record<string, unknown> = {}) {
  send(eventName, params);
}

export function AnalyticsEvents() {
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      const link = target.closest('a');
      if (!link) return;
      const href = link.getAttribute('href') ?? '';
      if (!href) return;

      if (/^https?:\/\/(wa\.me|api\.whatsapp\.com|web\.whatsapp\.com|whatsapp\.com)/i.test(href)) {
        send('whatsapp_clicked', { destination: href });
        return;
      }
      if (href === '/signup' || href.startsWith('/signup?') || href.startsWith('/signup#')) {
        send('signup_clicked', { cta: link.textContent?.trim().slice(0, 80) ?? '' });
        return;
      }
      if (href === '/login' || href.startsWith('/login?')) {
        send('login_clicked', { cta: link.textContent?.trim().slice(0, 80) ?? '' });
        return;
      }
      if (href.startsWith('/get-app') || /^https:\/\/(apps\.apple\.com|play\.google\.com)\//i.test(href)) {
        send('app_store_clicked', {
          store: /play\.google|store=android/i.test(href) ? 'google_play' : 'app_store',
          placement: link.dataset.placement ?? '',
        });
        return;
      }
      if (href.startsWith('mailto:')) {
        send('email_clicked', { destination: href });
        return;
      }
      if (href.startsWith('tel:')) {
        send('phone_clicked', { destination: href });
      }
    };

    document.addEventListener('click', onClick, { capture: true });
    return () => document.removeEventListener('click', onClick, { capture: true });
  }, []);

  return null;
}
